import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, authStore } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../components/icons';
import { Badge, Button, Card, Field, Input, Spinner } from '../components/ui';
import { Notice, Page, PageHeader, Section, Stack } from '../components/Page';

/**
 * The signed-in person's own account: address verification, workspaces they belong to,
 * and two-factor authentication.
 *
 * The enrolment flow shows the QR code and the manual key together. A desktop
 * authenticator has no camera, and offering only a QR code strands those users.
 */
export function Account() {
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mfa = useQuery({ queryKey: ['account', 'mfa'], queryFn: () => api.account.mfa() });
  const tenants = useQuery({ queryKey: ['account', 'tenants'], queryFn: () => api.account.tenants() });

  const resend = useMutation({
    mutationFn: () => api.account.resendVerification(),
    onError: (e: Error) => setError(e.message),
  });

  const switchTenant = useMutation({
    mutationFn: (tenantId: string) => api.account.switchTenant(tenantId),
    onSuccess: (result) => {
      // A switch mints a new token pair for the target workspace, so it is stored exactly
      // like a sign-in. A full reload is the honest way to drop every cached query that
      // belonged to the previous workspace.
      authStore.write({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresUtc: result.expiresUtc,
        user: result.user,
        tenant: result.tenant,
      });
      window.location.assign('/');
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Page>
      <PageHeader
        title="Your account"
        description={auth?.user.email}
      />

      <Stack>
        {error && <Notice tone="danger">{error}</Notice>}

        <Section
          title="Email address"
          hint="Used for sign-in, invitations and anything the system needs to tell you."
        >
        <div className="flex items-center gap-3">
          <span className="text-sm">{auth?.user.email}</span>
          <Button onClick={() => resend.mutate()} disabled={resend.isPending}>
            {resend.isPending ? 'Sending…' : 'Resend confirmation'}
          </Button>
        </div>
        {resend.data && (
          <p className="mt-2 text-xs text-[var(--ff-text-muted)]">
            {resend.data.alreadyVerified
              ? 'This address is already confirmed.'
              : resend.data.sent
                ? 'Sent. Check your inbox, including spam.'
                : 'This server has no email provider configured.'}
          </p>
        )}
        </Section>

      <MfaCard
        status={mfa.data}
        loading={mfa.isLoading}
        onChanged={() => void queryClient.invalidateQueries({ queryKey: ['account', 'mfa'] })}
        onError={setError}
      />

      <Section
        title="Workspaces"
        hint="Switching issues a new session for that workspace. Permissions are fixed when
              a session is issued, so a role change elsewhere only applies after switching
              again."
      >
        {tenants.isLoading && <Spinner label="Loading workspaces" />}

        <ul className="space-y-2">
          {(tenants.data?.data ?? []).map((t) => (
            <li
              key={t.id}
              className="flex items-center gap-3 rounded-md border border-[var(--ff-border)] p-2.5"
            >
              <Icon name="building" size={15} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{t.name}</div>
                <div className="text-xs text-[var(--ff-text-muted)]">
                  {t.role}
                  {t.requireMfa && ' · requires two-factor'}
                </div>
              </div>

              {t.isCurrent
                ? <Badge tone="primary">current</Badge>
                : (
                  <Button
                    onClick={() => switchTenant.mutate(t.id)}
                    disabled={switchTenant.isPending}
                  >
                    <Icon name="switch" size={14} /> Switch
                  </Button>
                )}
            </li>
          ))}
        </ul>
      </Section>

      <SessionsCard onError={setError} />
      </Stack>
    </Page>
  );
}

/**
 * Where this account is signed in, and how to stop being signed in there.
 *
 * The list is worth showing on its own — recognising a device you do not own is the point
 * — but the button is what makes it useful, and the button has to mean it: revoking here
 * kills the access token as well as the refresh token, so a session ends now rather than
 * whenever it next happens to renew.
 */
function SessionsCard({ onError }: { onError: (message: string) => void }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const sessions = useQuery({
    queryKey: ['account', 'sessions'],
    queryFn: () => api.sessions.list(),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] });

  const revoke = useMutation({
    mutationFn: (sessionId: string) => api.sessions.revoke(sessionId),
    onSuccess: refresh,
    onError: (e: Error) => onError(e.message),
  });

  const revokeAll = useMutation({
    mutationFn: () => api.sessions.revokeAll(false),
    onSuccess: () => { setConfirming(false); refresh(); },
    onError: (e: Error) => onError(e.message),
  });

  const rows = sessions.data?.data ?? [];
  const others = rows.filter((s) => !s.isCurrent);

  return (
    <Section
      title="Where you are signed in"
      hint="One entry per browser or app. Signing one out ends it immediately — it does not
            wait for the session to expire."
    >
      {sessions.isLoading && <Spinner label="Loading sessions" />}

      <ul className="space-y-2">
        {rows.map((session) => (
          <li
            key={session.id}
            className="flex items-center gap-3 rounded-md border border-[var(--ff-border)] p-2.5"
          >
            <Icon name="shield" size={15} />

            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {session.device}
                {session.isCurrent && ' — this device'}
              </div>
              <div className="text-xs text-[var(--ff-text-muted)]">
                {session.workspace && `${session.workspace} · `}
                signed in {new Date(session.signedInUtc).toLocaleString()}
                {' · last seen '}{new Date(session.lastSeenUtc).toLocaleString()}
              </div>
            </div>

            {session.isCurrent
              ? <Badge tone="primary">current</Badge>
              : (
                <Button
                  onClick={() => revoke.mutate(session.id)}
                  disabled={revoke.isPending}
                >
                  Sign out
                </Button>
              )}
          </li>
        ))}
      </ul>

      {others.length > 0 && (
        <div className="mt-4 border-t border-[var(--ff-border)] pt-3">
          {confirming ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">
                Sign out {others.length} other device{others.length === 1 ? '' : 's'}?
                You will stay signed in here.
              </span>
              <Button
                variant="danger"
                onClick={() => revokeAll.mutate()}
                disabled={revokeAll.isPending}
              >
                {revokeAll.isPending ? 'Signing out…' : 'Yes, sign them out'}
              </Button>
              <Button onClick={() => setConfirming(false)}>Cancel</Button>
            </div>
          ) : (
            <Button onClick={() => setConfirming(true)}>
              Sign out everywhere else
            </Button>
          )}
        </div>
      )}
    </Section>
  );
}

function MfaCard({
  status, loading, onChanged, onError,
}: {
  status?: { enabled: boolean; enrolling: boolean; recoveryCodesRemaining: number; requiredByWorkspace: boolean };
  loading: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [setup, setSetup] = useState<{ otpauthUri: string; manualKey: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);

  const enrol = useMutation({
    mutationFn: () => api.account.enrolMfa(),
    onSuccess: (result) => { setSetup(result); setCodes(null); },
    onError: (e: Error) => onError(e.message),
  });

  const confirm = useMutation({
    mutationFn: () => api.account.confirmMfa(code),
    onSuccess: (result) => {
      setSetup(null);
      setCode('');
      setCodes(result.recoveryCodes);
      onChanged();
    },
    onError: (e: Error) => onError(e.message),
  });

  const disable = useMutation({
    mutationFn: () => api.account.disableMfa(code),
    onSuccess: () => { setCode(''); onChanged(); },
    onError: (e: Error) => onError(e.message),
  });

  if (loading) return <Card className="p-4"><Spinner /></Card>;

  return (
    <Section
      title="Two-factor authentication"
      hint={status?.requiredByWorkspace
        ? 'This workspace requires it, so it cannot be turned off here.'
        : 'A code from an authenticator app, alongside your password.'}
      actions={status?.enabled
        ? <Badge tone="success">on</Badge>
        : <Badge tone="neutral">off</Badge>}
    >

      {/* ---- issued recovery codes -------------------------------------------- */}
      {codes && (
        <div className="mb-3 rounded-md border border-[var(--ff-primary)] p-3">
          <p className="mb-2 text-xs font-medium text-[var(--ff-danger)]">
            Save these now. They are shown once and cannot be retrieved again. Each works a
            single time if you lose your authenticator.
          </p>
          <div className="grid grid-cols-2 gap-1 font-mono text-sm">
            {codes.map((c) => <span key={c}>{c}</span>)}
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => void navigator.clipboard.writeText(codes.join('\n'))}>
              <Icon name="copy" size={14} /> Copy all
            </Button>
            <Button onClick={() => setCodes(null)}>I have saved them</Button>
          </div>
        </div>
      )}

      {/* ---- enrolment -------------------------------------------------------- */}
      {!status?.enabled && !setup && (
        <div>
          <p className="mb-3 text-xs text-[var(--ff-text-muted)]">
            A code from an authenticator app will be required alongside your password.
          </p>
          <Button variant="primary" onClick={() => enrol.mutate()} disabled={enrol.isPending}>
            {enrol.isPending ? 'Preparing…' : 'Set it up'}
          </Button>
        </div>
      )}

      {setup && (
        <div className="space-y-3">
          <p className="text-xs text-[var(--ff-text-muted)]">
            Scan this with your authenticator app, then enter the six-digit code it shows.
          </p>

          {/* Rendered by the app rather than fetched: sending the secret to a third-party
              chart service to draw a QR code would hand it to them. */}
          <QrCode value={setup.otpauthUri} />

          <details className="text-xs">
            <summary className="cursor-pointer text-[var(--ff-text-muted)]">
              Cannot scan? Enter this key by hand
            </summary>
            <code className="mt-2 block break-all rounded bg-[var(--ff-surface-sunken)] p-2
                             font-mono text-sm">
              {setup.manualKey}
            </code>
          </details>

          <div className="flex items-end gap-3">
            <Field label="Code from your app">
              <Input
                value={code}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Button
              variant="primary"
              onClick={() => confirm.mutate()}
              disabled={confirm.isPending || code.replace(/\D/g, '').length !== 6}
            >
              {confirm.isPending ? 'Checking…' : 'Turn it on'}
            </Button>
            <Button onClick={() => { setSetup(null); setCode(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* ---- already on ------------------------------------------------------- */}
      {status?.enabled && !codes && (
        <div className="space-y-3">
          <p className="text-xs text-[var(--ff-text-muted)]">
            {status.recoveryCodesRemaining} recovery code
            {status.recoveryCodesRemaining === 1 ? '' : 's'} left.
            {status.recoveryCodesRemaining <= 2 && ' Consider generating a fresh set.'}
          </p>

          {!status.requiredByWorkspace && (
            <div className="flex items-end gap-3">
              <Field
                label="Turn it off"
                hint="Enter a current code to confirm it is you."
              >
                <Input
                  value={code}
                  inputMode="numeric"
                  placeholder="000000"
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
              <Button
                variant="danger"
                onClick={() => disable.mutate()}
                disabled={disable.isPending || code.length < 6}
              >
                Turn off
              </Button>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/**
 * The QR code, rendered in the browser.
 *
 * `qrcode` rather than an <img> pointed at a QR service: the payload is a TOTP secret, and
 * sending it to a third party to be drawn would hand them the second factor. And a library
 * rather than a hand-rolled encoder, because a subtly wrong QR code is worse than none —
 * it silently enrols the wrong secret and locks the user out of their own account.
 */
function QrCode({ value, size = 180 }: { value: string; size?: number }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    QRCode.toDataURL(value, { width: size * 2, margin: 2, errorCorrectionLevel: 'M' })
      .then((url) => { if (!cancelled) setDataUrl(url); })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, [value, size]);

  // The manual key below is always shown, so a failure here is an inconvenience rather
  // than a dead end.
  if (failed) {
    return (
      <p className="text-xs text-[var(--ff-text-muted)]">
        The QR code could not be drawn. Enter the key below by hand instead.
      </p>
    );
  }

  if (!dataUrl) return <div style={{ width: size, height: size }} aria-hidden />;

  return (
    <img
      src={dataUrl}
      width={size}
      height={size}
      alt="QR code for your authenticator app"
      style={{ borderRadius: 8, background: '#fff' }}
    />
  );
}
